import { useState, useEffect, useRef, useCallback } from 'react';

export const useTimer = (onTimeout, inactivityThreshold = 5 * 60 * 1000) => {
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [inactivityTime, setInactivityTime] = useState(0);
  const [initialTime, setInitialTime] = useState(0);
  const stateRef = useRef({ deadline: 0, remaining: 0, initial: 0, running: false, expired: false, lastActivity: Date.now() });
  const timeoutRef = useRef(onTimeout);
  timeoutRef.current = onTimeout;

  const finish = useCallback((notify) => {
    const state = stateRef.current;
    if (state.expired) return;
    state.running = false;
    state.expired = true;
    state.remaining = 0;
    setTime(0);
    setIsActive(false);
    setIsExpired(true);
    setInactivityTime(0);
    if (notify) timeoutRef.current?.();
  }, []);

  const tick = useCallback(() => {
    const state = stateRef.current;
    if (!state.running) return;
    const now = Date.now();
    state.remaining = Math.max(0, state.deadline - now);
    if (!state.remaining) {
      finish(true);
      return;
    }
    setTime(state.remaining);
    setInactivityTime(Math.floor(Math.max(0, now - state.lastActivity) / 1000) * 1000);
  }, [finish]);

  useEffect(() => {
    if (!isActive) return;
    tick();
    const interval = setInterval(tick, 100);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isActive, tick]);

  const start = useCallback((customTime) => {
    const duration = Math.max(0, Number(customTime ?? stateRef.current.initial) || 0);
    stateRef.current = { deadline: Date.now() + duration, remaining: duration, initial: duration, running: true, expired: false, lastActivity: Date.now() };
    setInitialTime(duration);
    setTime(duration);
    setIsActive(true);
    setIsExpired(false);
    setInactivityTime(0);
  }, []);

  const pause = useCallback(() => {
    const state = stateRef.current;
    if (!state.running) return;
    tick();
    state.running = false;
    setIsActive(false);
  }, [tick]);

  const resume = useCallback(() => {
    const state = stateRef.current;
    if (state.expired || state.running) return;
    state.deadline = Date.now() + state.remaining;
    state.lastActivity = Date.now();
    state.running = true;
    setIsActive(true);
    setInactivityTime(0);
  }, []);

  const reset = useCallback(() => {
    const state = stateRef.current;
    state.running = false;
    state.expired = false;
    state.remaining = state.initial;
    setTime(state.initial);
    setIsActive(false);
    setIsExpired(false);
    setInactivityTime(0);
  }, []);

  const resetInactivity = useCallback(() => {
    stateRef.current.lastActivity = Date.now();
    setInactivityTime(0);
  }, []);

  const expire = useCallback(() => finish(false), [finish]);

  const skipTime = useCallback((milliseconds) => {
    const state = stateRef.current;
    const amount = Math.max(0, Number(milliseconds) || 0);
    if (!state.running || state.expired) return;
    state.deadline -= amount;
    tick();
  }, [tick]);

  const totalSeconds = Math.ceil(time / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    time,
    formattedTime: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
    initialTime,
    progress: initialTime ? ((initialTime - time) / initialTime) * 100 : 0,
    isActive, isExpired, start, pause, resume, reset, resetInactivity, expire, skipTime,
    inactivityMinutes: Math.floor(inactivityTime / 60000),
    shouldWarn: isActive && !isExpired && inactivityTime >= inactivityThreshold,
    timeLeft: time,
    isWarning: time <= 5 * 60 * 1000 && time > 0
  };
};
